import { useMutation } from '@tanstack/react-query'

import { postRequest } from 'src/lib/api-client'
import type { LeadDistributionPreference } from 'src/shared/features/enterprise/api/use-update-enterprise-settings'

export interface EnterpriseBusinessImportPreviewParams {
  enterpriseId: number
  file: File
  lynkEnabledForFile?: boolean
  distributionSystemId?: number | null
  leadDistributionPreference?: LeadDistributionPreference | null
}

export interface EnterpriseBusinessImportPreviewMapping {
  clientField: string
  uploadedColumn: string | null
  backendField: string
  status: 'mapped' | 'unmapped'
  matchedColumns: string[]
}

export interface EnterpriseBusinessImportPreviewColumnMapping {
  sourceField: string
  normalizedSourceField: string
  canonicalKey: string | null
  mappedTo: string | null
  targetFields: string[]
  status: 'mapped' | 'custom_field' | 'ignored_sensitive' | 'ignored_excluded'
}

export interface EnterpriseBusinessImportPreviewSummary {
  totalRows: number
  totalColumns: number
  mapped: number
  customField: number
  ignoredSensitive: number
  ignoredExcluded: number
  friendlyMapped: number
  friendlyUnmapped: number
}

export interface EnterpriseBusinessImportPreviewResponse {
  success: boolean
  uploadId: string
  lynkEnabledForFile: boolean
  closedAtColumnExists: boolean
  detectedClosedAtColumns: string[]
  requiresClosedBusinessesConfirmation: boolean
  distributionSystemId: number | null
  leadDistributionPreference: LeadDistributionPreference | null
  summary: EnterpriseBusinessImportPreviewSummary
  mappings: EnterpriseBusinessImportPreviewMapping[]
  columnMappings: EnterpriseBusinessImportPreviewColumnMapping[]
}

/**
 * Preview an enterprise business import file and receive staging metadata + inferred column mappings.
 */
const previewEnterpriseBusinessImport = async ({
  enterpriseId,
  file,
  lynkEnabledForFile,
  distributionSystemId,
  leadDistributionPreference,
}: EnterpriseBusinessImportPreviewParams) => {
  const formData = new FormData()
  formData.append('file', file)
  if (lynkEnabledForFile !== undefined && lynkEnabledForFile !== null) {
    formData.append('lynk_enabled_for_file', String(lynkEnabledForFile))
  }

  if (distributionSystemId !== undefined && distributionSystemId !== null) {
    formData.append('distribution_system_id', String(distributionSystemId))
  }

  if (leadDistributionPreference !== undefined && leadDistributionPreference !== null) {
    formData.append('lead_distribution_preference', leadDistributionPreference)
  }

  return postRequest<EnterpriseBusinessImportPreviewResponse>(
    `/enterprise/${enterpriseId}/business-import/preview`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
}

export default function useEnterpriseBusinessImportPreview() {
  return useMutation({
    mutationFn: async (params: EnterpriseBusinessImportPreviewParams) => {
      const response = await previewEnterpriseBusinessImport(params)

      if (!response) {
        throw new Error('No response received from enterprise business import preview')
      }

      return response
    },
  })
}
